import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client';
import { ApolloClient, createHttpLink, gql, InMemoryCache } from '@apollo/client/core';
import { Observable, from, switchMap } from 'rxjs';
import type { ApolloLink } from '@apollo/client/link';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';

const REFRESH_TOKEN_MUTATION = gql`
  mutation refreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      tokens {
        access { token }
        refresh { token }
      }
    }
  }
`;

interface RefreshTokenData {
  refreshToken: {
    tokens: {
      access: { token: string };
      refresh: { token: string };
    };
  };
}

let isRefreshing = false;
let pendingRequests: Array<() => void> = [];

const getItem = (key: string): string | null => {
  const value = localStorage.getItem(key);
  try {
    return JSON.parse(value as string) as string;
  } catch {
    return null;
  }
};

const setItem = (key: string, value: string): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

const resolvePendingRequests = (): void => {
  pendingRequests.forEach(cb => cb());
  pendingRequests = [];
};

const getNewToken = async (): Promise<void> => {
  const refreshToken = getItem('refresh_token');

  const renewClient = new ApolloClient({
    link: createHttpLink({ uri: environment.graphqlURI }),
    cache: new InMemoryCache(),
  });

  const result = await renewClient.mutate<RefreshTokenData>({
    mutation: REFRESH_TOKEN_MUTATION,
    variables: { refreshToken },
  });

  setItem('access_token', result.data!.refreshToken.tokens.access.token);
  setItem('refresh_token', result.data!.refreshToken.tokens.refresh.token);
};

const errorLink = new ErrorLink(({ error, operation, forward }): Observable<ApolloLink.Result> | void => {
  if (CombinedGraphQLErrors.is(error)) {
    const authError = error.errors.find(err => err?.message === 'Please authenticate');

    if (authError) {
      if (!isRefreshing) {
        isRefreshing = true;

        return from(
          getNewToken().catch(() => {
            resolvePendingRequests();
            isRefreshing = false;
            // localStorage.clear();
            location.href = '/login';
          })
        ).pipe(
          switchMap(() => {
            const currentHeaders = (operation.getContext().headers as HttpHeaders) ?? new HttpHeaders();
            operation.setContext({
              headers: currentHeaders.set('Authorization', `Bearer ${getItem('access_token')}`),
            });
            resolvePendingRequests();
            isRefreshing = false;
            return forward(operation);
          })
        ) as Observable<ApolloLink.Result>;
      } else {
        return new Observable<ApolloLink.Result>(observer => {
          pendingRequests.push(() => {
            forward(operation).subscribe(observer);
          });
        });
      }
    }

    error.errors.forEach(err => {
      if (err?.message !== 'Please authenticate') {
        console.error('[GraphQL error]:', err.message, err.locations, err.path);
      }
    });
  } else if (error) {
    console.error('[Network error]:', error);
  }
});

export default errorLink;
